import { useEffect, useState, lazy, Suspense } from "react"
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom"
import { Box, CircularProgress, CssBaseline, GlobalStyles, ThemeProvider } from "@mui/material"

import Theme from "./assets/Theme"
import Popup from "./components/Popup"
import EnquireModal from "./components/EnquireModal"
import PageViewTracker from "./components/PageViewTracker"
import ErrorBoundary from "./components/ErrorBoundary"
import { homeCopy } from "./content/homeCopy"
import DefaultRoutes from "./routes/DefaultRoutes"
import Footer from "./components/Footer"
import { initAos } from "./utils/aos"
import adminTheme from "./admin/theme/adminTheme"
import { AuthProvider } from "./admin/features/auth/hooks/AuthProvider"
import RequireAuthRoute from "./admin/features/auth/hooks/RequireAuthRoute"

const LoginPage = lazy(() => import("./admin/features/auth/pages/LoginPage"))
const AdminApp = lazy(() => import("./admin/AdminApp"))

const marketingOverflowStyles = {
    html: {
        overflowX: "hidden",
        maxWidth: "100%",
    },
    body: {
        overflowX: "hidden",
        maxWidth: "100%",
    },
    "#root": {
        overflowX: "hidden",
        maxWidth: "100%",
    },
    // Safety net: when AOS is disabled (mobile / reduced motion), aos.css still
    // sets opacity:0 on [data-aos] until .aos-animate — which never arrives for
    // vehicles loaded after init. Force visible below the same 900px breakpoint.
    "@media (max-width: 899.98px)": {
        "[data-aos]": {
            opacity: "1 !important",
            transform: "none !important",
            pointerEvents: "auto !important",
        },
    },
    "@media (prefers-reduced-motion: reduce)": {
        "[data-aos]": {
            opacity: "1 !important",
            transform: "none !important",
            pointerEvents: "auto !important",
        },
    },
}

const AdminLoading = () => (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress color="secondary" />
    </Box>
)

const MarketingShell = () => {
    const location = useLocation()
    const isAdminRoute = location.pathname.startsWith("/admin")

    return (
        <>
            <PageViewTracker />
            <DefaultRoutes />
            {!isAdminRoute && (
                <ThemeProvider theme={Theme.Dark}>
                    <Footer />
                </ThemeProvider>
            )}
        </>
    )
}

const App = () => {
    useEffect(() => {
        initAos()
    }, [])

    useEffect(() => {
        const meta = document.querySelector('meta[name="description"]')
        if (meta) {
            meta.setAttribute("content", homeCopy.meta.description)
        }
    }, [])

    return (
        <Router>
            <ErrorBoundary>
                <AuthProvider>
                    <Routes>
                        <Route
                            path="/admin/login"
                            element={
                                <ThemeProvider theme={adminTheme}>
                                    <CssBaseline />
                                    <Suspense fallback={<AdminLoading />}>
                                        <LoginPage />
                                    </Suspense>
                                </ThemeProvider>
                            }
                        />
                        <Route
                            path="/admin/*"
                            element={
                                <RequireAuthRoute>
                                    <ThemeProvider theme={adminTheme}>
                                        <CssBaseline />
                                        <Suspense fallback={<AdminLoading />}>
                                            <AdminApp />
                                        </Suspense>
                                    </ThemeProvider>
                                </RequireAuthRoute>
                            }
                        />
                        <Route
                            path="/*"
                            element={
                                <ThemeProvider theme={Theme.Light}>
                                    <CssBaseline />
                                    <GlobalStyles styles={marketingOverflowStyles} />
                                    <Popup>
                                        <EnquireModal>
                                            <MarketingShell />
                                        </EnquireModal>
                                    </Popup>
                                </ThemeProvider>
                            }
                        />
                    </Routes>
                </AuthProvider>
            </ErrorBoundary>
        </Router>
    )
}

export default App
